import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import api from '../../services/api';

interface MaintenanceScheduleProps {
  schedules: any[];
  equipment: any[];
  onUpdate: () => void;
  showCompleted?: boolean;
}

const MaintenanceSchedule: React.FC<MaintenanceScheduleProps> = ({
  schedules,
  equipment,
  onUpdate,
  showCompleted = false,
}) => {
  const { t } = useTranslation();
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const getEquipmentInfo = (equipmentId: string) => {
    const equip = equipment.find(e => e.id === equipmentId);
    return equip ? `${equip.brand} ${equip.model}` : 'Unknown';
  };

  const getUrgencyChip = (schedule: any) => {
    const now = new Date();
    const dueDate = new Date(schedule.due_date);
    const daysUntil = differenceInDays(dueDate, now);

    if (schedule.status === 'completed') {
      return (
        <Chip
          size="small"
          label={t('reminders.maintenance.status.completed')}
          color="success"
          icon={<CheckCircleIcon />}
        />
      );
    }

    if (daysUntil < 0) {
      return (
        <Chip
          size="small"
          label={t('reminders.maintenance.urgency.overdue')}
          color="error"
          icon={<WarningIcon />}
        />
      );
    }

    if (daysUntil === 0) {
      return (
        <Chip
          size="small"
          label={t('reminders.maintenance.urgency.dueToday')}
          color="error"
        />
      );
    }

    if (daysUntil === 1) {
      return (
        <Chip
          size="small"
          label={t('reminders.maintenance.urgency.dueTomorrow')}
          color="warning"
        />
      );
    }

    if (daysUntil <= 7) {
      return (
        <Chip
          size="small"
          label={t('reminders.maintenance.urgency.dueThisWeek')}
          color="warning"
        />
      );
    }

    if (daysUntil <= 30) {
      return (
        <Chip
          size="small"
          label={t('reminders.maintenance.urgency.dueThisMonth')}
          color="info"
        />
      );
    }

    return (
      <Chip
        size="small"
        label={t('reminders.maintenance.urgency.upcoming')}
        color="default"
      />
    );
  };

  const handleCompleteClick = (schedule: any) => {
    setSelectedSchedule(schedule);
    setCompleteNotes('');
    setCompleteDialogOpen(true);
  };

  const handleCompleteConfirm = async () => {
    if (!selectedSchedule) return;

    try {
      setLoading(true);
      await api.put(`/reminders/schedules/${selectedSchedule.id}`, {
        completed_date: new Date().toISOString(),
        completed_notes: completeNotes,
      });
      setCompleteDialogOpen(false);
      setSelectedSchedule(null);
      onUpdate();
    } catch (error) {
      console.error('Error completing maintenance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (schedule: any) => {
    setSelectedSchedule(schedule);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSchedule) return;

    try {
      setLoading(true);
      await api.delete(`/reminders/schedules/${selectedSchedule.id}`);
      setDeleteDialogOpen(false);
      setSelectedSchedule(null);
      onUpdate();
    } catch (error) {
      console.error('Error deleting schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedSchedules = [...schedules].sort((a, b) => {
    if (showCompleted) {
      return new Date(b.completed_date || b.due_date).getTime() - 
             new Date(a.completed_date || a.due_date).getTime();
    }
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });

  if (sortedSchedules.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {showCompleted 
            ? t('reminders.maintenance.noHistory')
            : t('reminders.maintenance.noUpcoming')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('reminders.equipment.type')}</TableCell>
              <TableCell>{t('reminders.maintenance.type')}</TableCell>
              <TableCell>
                {showCompleted 
                  ? t('reminders.maintenance.completedDate')
                  : t('reminders.maintenance.dueDate')}
              </TableCell>
              <TableCell>{t('reminders.maintenance.statusLabel')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSchedules.map((schedule) => (
              <TableRow key={schedule.id}>
                <TableCell>
                  <Typography variant="body2">
                    {getEquipmentInfo(schedule.equipment_id)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {schedule.equipment?.equipment_type?.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {t(`reminders.maintenance.types.${schedule.maintenance_type}`) || schedule.maintenance_type}
                  </Typography>
                  {schedule.description && (
                    <Typography variant="caption" color="text.secondary">
                      {schedule.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {showCompleted && schedule.completed_date ? (
                    <Typography variant="body2">
                      {format(new Date(schedule.completed_date), 'dd/MM/yyyy')}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {format(new Date(schedule.due_date), 'dd/MM/yyyy')}
                    </Typography>
                  )}
                  {schedule.is_recurring && (
                    <Chip
                      size="small"
                      label={t('reminders.maintenance.recurring')}
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {getUrgencyChip(schedule)}
                </TableCell>
                <TableCell align="right">
                  {!showCompleted && schedule.status !== 'completed' && (
                    <Tooltip title={t('reminders.maintenance.markComplete')}>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleCompleteClick(schedule)}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t('reminders.maintenance.edit')}>
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('reminders.maintenance.delete')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(schedule)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Complete Maintenance Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('reminders.maintenance.markComplete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            {t('reminders.maintenance.completeConfirm')} {selectedSchedule?.maintenance_type}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('reminders.maintenance.notes')}
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
            placeholder={t('reminders.maintenance.completionNotesPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCompleteConfirm}
            color="success"
            variant="contained"
            disabled={loading}
          >
            {t('reminders.maintenance.markComplete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('reminders.maintenance.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('reminders.maintenance.confirmDelete')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceSchedule;